import * as React from "react";
import { makeStyles } from "@fluentui/react-components";
import { Router } from "./Router";
import { RouteProvider, useRoute } from "@/context/router";
import { AuthProvider, useAuth } from "@/context/auth";
import { PostHogProvider } from "posthog-js/react";
import { POSTHOG_PUBLIC_HOST, POSTHOG_API_KEY } from "@/constants";
import { PostHogConfig } from "posthog-js";
import { AnalyticsKey } from "@/types/analytics";
import { trackEvent } from "@/services/analytics-service";
import { getDocumentProperties } from "@/taskpane/lib/common/document";
import { ErrorFallback } from "@/components/error-boundary";
import { Toaster } from "react-hot-toast";
import * as Sentry from "@sentry/react";
import posthog from "posthog-js";
import { DocumentClassificationProvider } from "@/context/document-classification";
import { getFromStorage } from "@/taskpane/lib";
import { OktaProvider } from "@/context/okta";
import { QuestionsProvider } from "@/context/questions";
import { NewClauseProvider } from "@/context/new-clause";
import { RedraftClauseProvider } from "@/context/redraft-clause";

// This works for the widget
// export const config = {
//   oidc: {
//     issuer: "https://dev-39122184.okta.com/oauth2/default",
//     clientId: "0oanwtkjrkVX13vKp5d7",
//     scopes: ["openid", "profile", "email", "offline_access"],
//     redirectUri: `https://localhost:3000/login/callback`,
//   } as OktaAuthOptions,
//   widget: {
//     issuer: "https://dev-39122184.okta.com/oauth2/default",
//     clientId: "0oanwtkjrkVX13vKp5d7",
//     scopes: ["openid", "profile", "email", "offline_access"],
//     redirectUri: `https://localhost:3000/login/callback`,
//   } as WidgetOptions,
// };

// This works for the popup
// export const config = {
//   oidc: {
//     issuer: "https://dev-39122184.okta.com/oauth2/default",
//     clientId: "0oanxwod3paW5Y20o5d7",
//     scopes: ["openid", "profile", "email", "offline_access"],
//     redirectUri: "https://localhost:3000/login/callback",
//     pkce: true,
//   } as OktaAuthOptions,
// };

// const oktaAuth = new OktaAuth({ ...config.oidc });

const useStyles = makeStyles({
  root: {
    minHeight: "100vh",
    padding: "10px",
    fontSize: "14px",
  },
});

const posthogOptions: Partial<PostHogConfig> = {
  api_host: POSTHOG_PUBLIC_HOST,
  autocapture: true,
};

const AppContent: React.FC = () => {
  const styles = useStyles();
  const { isAuthenticated, user, isLoading } = useAuth();
  const { setCurrentRoute } = useRoute();

  const trackAddinOpen = async () => {
    trackEvent(AnalyticsKey.ADDIN.OPEN, {
      documentInfo: await getDocumentProperties(),
    });
  };

  React.useEffect(() => {
    trackAddinOpen();
  }, []);

  React.useEffect(() => {
    if (isLoading) return;
    const loadRoute = async () => {
      if (isAuthenticated) {
        posthog.identify(user.user.id, user.user);
        setCurrentRoute("risk-analysis");
      } else {
        const showed = await getFromStorage("showedFRE");
        if (!showed || showed === "false") {
          setCurrentRoute("FRE");
          return;
        }
        setCurrentRoute("login");
      }
    };

    loadRoute();
  }, [isAuthenticated, isLoading]);

  return (
    <div className={styles.root}>
      <Router />
      <Toaster />
    </div>
  );
};

const App: React.FC = () => {
  return (
    <PostHogProvider options={posthogOptions} apiKey={POSTHOG_API_KEY}>
      <Sentry.ErrorBoundary fallback={({ error }) => <ErrorFallback error={error} />}>
        <OktaProvider>
          <AuthProvider>
            <RouteProvider>
              <DocumentClassificationProvider>
                <QuestionsProvider>
                  <NewClauseProvider>
                    <RedraftClauseProvider>
                      <AppContent />
                    </RedraftClauseProvider>
                  </NewClauseProvider>
                </QuestionsProvider>
              </DocumentClassificationProvider>
            </RouteProvider>
          </AuthProvider>
        </OktaProvider>
      </Sentry.ErrorBoundary>
    </PostHogProvider>
  );
};

export default App;
